import React, { ComponentType } from 'react';
import styled from '@emotion/styled';
import { ITheme } from '../../../../../../ui';
import { GrandTotal } from './GrandTotal';
import { RefundTotal } from './RefundTotal';
import { OriginalAndNewTotal } from './OriginalAndNewTotal';
import { IPrice } from '../../../../../shared/i18n/price/IPrice';
import { BadgeTag } from '../../../../../account/ui/shared/BadgeTag';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { useAuthStore } from '../../../../../auth/shared/store/useAuthStore';
import { useFormatNumberWithSeparator } from '../../../../../shared/utils/useFormatNumberWithSeparator';
import Responsive from '../../../../../../ui/utils/Responsive';
import { getDifference } from '../../../../../shared/i18n/price/utils/getDifference';
import { IBookingSummarySubmitProps } from './IBookingSummarySubmitProps';
import { getShouldProceedWithoutPay } from '../../../../domain/booking/models/helpers/getShouldProceedWithoutPay';

export const StyledBookingSummaryFooter = styled.div<{}, ITheme>`
	display: flex;
	justify-content: space-between;
	flex-direction: column;
`;

interface IBookingSummaryFooterProps {
	originalTotalPrice?: IPrice;
	totalPrice: IPrice;
	loyaltyPoints?: number;
	Submit?: ComponentType<IBookingSummarySubmitProps>;
}

export const BookingSummaryFooter = ({
	originalTotalPrice,
	totalPrice,
	loyaltyPoints,
	Submit,
}: IBookingSummaryFooterProps) => {
	const { t } = useTranslation();
	const { isAuthenticated } = useAuthStore();
	const { formatWithSeparator } = useFormatNumberWithSeparator();

	const priceDifference =
		originalTotalPrice && getDifference(totalPrice, originalTotalPrice);

	const shouldProceedWithoutPay = getShouldProceedWithoutPay(
		originalTotalPrice,
		totalPrice,
	);

	const isRefund = priceDifference && priceDifference?.centAmount < 0;

	return (
		<>
			{originalTotalPrice && (
				<OriginalAndNewTotal
					originalTotalPrice={originalTotalPrice}
					totalPrice={totalPrice}
				/>
			)}
			<StyledBookingSummaryFooter>
				{isRefund ? (
					<RefundTotal priceDifference={priceDifference} />
				) : (
					<GrandTotal
						totalPrice={totalPrice}
						priceDifference={priceDifference}
					/>
				)}

				{Submit && <Submit shouldProceedWithoutPay={shouldProceedWithoutPay} />}

				{isAuthenticated && !!loyaltyPoints && (
					<StyledEarningLoyaltyPoints>
						<span>{t('loyalty_booking_summary_earning_label')}</span>
						<BadgeTag
							text={t('loyalty_offer_points_template', {
								points: formatWithSeparator(loyaltyPoints),
							})}
						/>
						<span>
							{t('loyalty_booking_summary_earning_with_booking_label')}
						</span>
					</StyledEarningLoyaltyPoints>
				)}
			</StyledBookingSummaryFooter>
		</>
	);
};

export const StyledEarningLoyaltyPoints = styled.div`
	width: 100%;
	display: flex;
	margin-top: 12px;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	gap: 4px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		font-size: 13px;
	}
`;
